<template>
    <div>
        <HeadlessTabGroup :selectedIndex="selectedTab" @change="changeTab">
            <HeadlessTabList>
                <div class="items-center" :class="hasLeftSlot || hasRightSlot ? 'grid grid-cols-3' : ''">
                    <div v-if="hasLeftSlot">
                        <slot name="left" />
                    </div>

                    <div
                        class="place-self-center w-fit mx-auto text-center"
                        :class="[type === 'rounded' ? '' : 'bg-gray-900 p-2 rounded-lg', $attrs.tabTitleClass]"
                    >
                        <template v-for="(tabTitle, tabTitleIndex) in items">
                            <HeadlessTab
                                :id="tabTitle.id ? tabTitle.id : tabTitle.title"
                                as="span"
                                v-slot="{ selected }"
                                class="outline-none"
                            >
                                <template v-if="hasTabTitleSlot">
                                    <slot name="tabTitle" v-bind:item="{ ...tabTitle }" v-bind:selected="selected" />
                                </template>

                                <template v-else>
                                    <button
                                        :id="tabTitle.id ? tabTitle.id : tabTitle.title"
                                        class="px-4 py-2 text-body-md outline-none transition-all"
                                        :class="[
                                            selected ? 'bg-brand-transparent-40  text-gray-100' : 'text-gray-500',
                                            type === 'rounded' ? 'rounded-full' : 'rounded-md',
                                        ]"
                                    >
                                        <Icon v-if="tabTitle.titleIcon" :name="tabTitle.titleIcon" size="24px" />
                                        {{ tabTitle.title }}
                                    </button>
                                </template>
                            </HeadlessTab>
                        </template>
                    </div>

                    <div class="place-self-end" v-if="hasRightSlot">
                        <slot name="right" />
                    </div>
                </div>
            </HeadlessTabList>
            <HeadlessTabPanels :id="id" class="mt-4">
                <HeadlessTabPanel
                    :id="tabContent.title"
                    v-for="(tabContent, tabContentIndex) in items"
                    class="transition-all"
                >
                    <template v-if="hasTabContentSlot">
                        <slot name="tabContent" v-bind:item="{ ...tabContent }" />
                    </template>

                    <template v-else>
                        <div v-if="typeof tabContent.content === 'string'">
                            {{ tabContent.content }}
                        </div>

                        <component v-if="typeof tabContent.content === 'object'" :is="tabContent.content" />
                    </template>
                </HeadlessTabPanel>
            </HeadlessTabPanels>
        </HeadlessTabGroup>
    </div>
</template>

<script setup lang="ts">
import type { ITabItem } from "~/types/components/tab.type";

interface IProps {
    items: ITabItem<unknown>[];
    type?: "rounded" | "default";
}

const slots = useSlots();

const id = useId();

const hasTabContentSlot = computed(() => !!slots.tabContent);

const hasLeftSlot = computed(() => !!slots.left);

const hasRightSlot = computed(() => !!slots.right);

const hasTabTitleSlot = computed(() => !!slots.tabTitle);

defineProps<IProps>();

const selectedTab = defineModel<number>({ default: 0, required: false });

function changeTab(index: number) {
    selectedTab.value = index;
}
</script>
